import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiAward, FiClock, FiTarget, FiStar, FiRefreshCw, FiHome, FiShare2 } from 'react-icons/fi';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/common/Badge';
import { testAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useAppConfig } from '../../contexts/AppConfigContext';

const GameResults = () => {
  const { gameId, resultId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showError, showSuccess } = useNotification();
  const { appName } = useAppConfig();

  const fetchGameResult = useCallback(async () => {
    try {
      setLoading(true);
      const response = await testAPI.getTestResult(resultId);
      setResult(response.data);
    } catch (error) {
      showError('Failed to load game results');
      navigate('/games');
    } finally {
      setLoading(false);
    }
  }, [resultId, showError, navigate]);

  useEffect(() => {
    fetchGameResult();
  }, [fetchGameResult]);

  const calculatePerformanceLevel = (score) => {
    if (score >= 90) return { level: 'Excellent', color: 'success', icon: FiAward };
    if (score >= 80) return { level: 'Great', color: 'primary', icon: FiStar };
    if (score >= 70) return { level: 'Good', color: 'info', icon: FiTarget };
    if (score >= 60) return { level: 'Fair', color: 'warning', icon: FiClock };
    return { level: 'Needs Improvement', color: 'danger', icon: FiRefreshCw };
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handlePlayAgain = () => {
    navigate(`/games/${gameId}/play`);
  };

  const handleShareResults = async () => {
    try {
      const shareData = {
        title: `${appName} Game Results - ${result.score}%`,
        text: `I just scored ${result.score}% in ${result.game?.title || 'a game'} on ${appName}!`,
        url: window.location.href
      };
      
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
        showSuccess('Results copied to clipboard!');
      }
    } catch (error) {
        // Sharing failed - could implement fallback or user notification
        if (process.env.NODE_ENV === 'development') {
          console.log('Sharing failed:', error);
        }
      }
  };

  if (loading) {
    return (
      <div className="page-container">
        <LoadingSpinner text="Loading results..." />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="page-container">
        <div className="error-state">
          <h2>Results not found</h2>
          <p>The game results you're looking for don't exist.</p>
          <Button onClick={() => navigate('/games')}>Back to Games</Button>
        </div>
      </div>
    );
  }

  const performance = calculatePerformanceLevel(result.score);
  const PerformanceIcon = performance.icon;

  return (
    <div className="page-container">
      <div className="game-results-container">
        {/* Header */}
        <div className="results-header">
          <div className="performance-badge">
            <PerformanceIcon size={48} className={`performance-icon performance-icon--${performance.color}`} />
            <h1 className="performance-level">{performance.level}!</h1>
          </div>
          <h2 className="game-title">{result.game?.title || 'Game Complete'}</h2>
        </div>

        {/* Score Card */}
        <div className="score-card">
          <div className="score-display">
            <div className="score-number">{result.score}%</div>
            <div className="score-label">Final Score</div>
          </div>
          
          <div className="score-breakdown">
            <div className="score-stat">
              <div className="stat-value">{result.correctAnswers}</div>
              <div className="stat-label">Correct</div>
            </div>
            <div className="score-stat">
              <div className="stat-value">{result.totalQuestions - result.correctAnswers}</div>
              <div className="stat-label">Incorrect</div>
            </div>
            <div className="score-stat">
              <div className="stat-value">{result.totalQuestions}</div>
              <div className="stat-label">Total</div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FiClock size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-title">Time Taken</div>
              <div className="stat-value">{formatTime(result.timeTaken)}</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <FiTarget size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-title">Accuracy</div>
              <div className="stat-value">{Math.round((result.correctAnswers / result.totalQuestions) * 100)}%</div>
            </div>
          </div>
          
          {result.averageTimePerQuestion && (
            <div className="stat-card">
              <div className="stat-icon">
                <FiClock size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-title">Avg. Time/Question</div>
                <div className="stat-value">{formatTime(result.averageTimePerQuestion)}</div>
              </div>
            </div>
          )}
          
          {result.streak && (
            <div className="stat-card">
              <div className="stat-icon">
                <FiStar size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-title">Best Streak</div>
                <div className="stat-value">{result.streak}</div>
              </div>
            </div>
          )}
        </div>

        {/* Subject Breakdown */}
        {result.subjectBreakdown && result.subjectBreakdown.length > 0 && (
          <div className="subject-breakdown">
            <h3>Performance by Subject</h3>
            <div className="subject-stats">
              {result.subjectBreakdown.map((subject, index) => (
                <div key={index} className="subject-stat">
                  <div className="subject-name">{subject.name}</div>
                  <div className="subject-score">
                    <div className="score-bar">
                      <div 
                        className="score-fill" 
                        style={{ width: `${subject.percentage}%` }}
                      ></div>
                    </div>
                    <span className="score-text">{subject.percentage}%</span>
                  </div>
                  <div className="subject-details">
                    {subject.correct}/{subject.total} correct
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Achievements */}
        {result.achievements && result.achievements.length > 0 && (
          <div className="achievements-section">
            <h3>Achievements Unlocked</h3>
            <div className="achievements-grid">
              {result.achievements.map((achievement, index) => (
                <div key={index} className="achievement-card">
                  <div className="achievement-icon">
                    <FiAward size={24} />
                  </div>
                  <div className="achievement-content">
                    <div className="achievement-title">{achievement.title}</div>
                    <div className="achievement-description">{achievement.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {result.recommendations && result.recommendations.length > 0 && (
          <div className="recommendations-section">
            <h3>Recommended Next Steps</h3>
            <div className="recommendations-list">
              {result.recommendations.map((rec, index) => (
                <div key={index} className="recommendation-item">
                  <Badge variant="info">{rec.type}</Badge>
                  <span>{rec.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="results-actions">
          <Button
            variant="secondary"
            onClick={() => navigate('/games')}
          >
            <FiHome size={16} />
            Back to Games
          </Button>
          
          <Button
            variant="outline"
            onClick={handleShareResults}
          >
            <FiShare2 size={16} />
            Share Results
          </Button>
          
          <Button
            variant="primary"
            onClick={handlePlayAgain}
          >
            <FiRefreshCw size={16} />
            Play Again
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GameResults;